import { useRepositoryUploadStates } from '#pplx/frontend/apps/ask/connectors/hooks/useRepositoryUploadStates.js';
import { useUploadProgressContext } from '#pplx/frontend/apps/ask/queries/file-queries/uploadProgressContext.js';
import { getListConnectorFilesQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/fileKeys.js';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
export const useUploadProgress = ({ fileRepoInfo, reason, enablePolling = true, }) => {
    const { isMonitoryingFileRepository, getLocalProgress, clearLocalFileUploads } = useUploadProgressContext();
    const queryClient = useQueryClient();
    const [finished, setFinished] = useState(false);
    // Track max totals separately for repo and local uploads
    const [maxRepoTotal, setMaxRepoTotal] = useState(0);
    const [maxLocalTotal, setMaxLocalTotal] = useState(0);
    const [maxErrors, setMaxErrors] = useState(0);
    const [hadProjectImportWorkLimitExceeded, setHadProjectImportWorkLimitExceeded] = useState(false);
    // Get repository upload states
    const { total: repoTotal, pending: repoPending, errors: repoErrors, projectOverSizeLimit = false, projectImportWorkLimitExceeded = false, } = useRepositoryUploadStates({
        enablePolling: enablePolling,
        forceActivePolling: isMonitoryingFileRepository(fileRepoInfo),
        fileRepoInfo,
        reason,
    });
    // Get local file upload progress
    const localProgress = getLocalProgress(fileRepoInfo);
    // Combine repository and local progress
    const total = maxRepoTotal + maxLocalTotal;
    const pending = repoPending + localProgress.pending;
    const errors = repoErrors + localProgress.errors;
    const uploaded = total - pending;
    const newUploadCycleStarted = finished && (repoTotal > 0 || localProgress.pending > 0);
    const retainedProjectImportWorkLimitExceeded = projectImportWorkLimitExceeded || (hadProjectImportWorkLimitExceeded && !newUploadCycleStarted);
    // Track max totals and detect when upload finishes
    useEffect(() => {
        // Track max values during upload
        if (repoTotal > maxRepoTotal) {
            setMaxRepoTotal(repoTotal);
        }
        if (localProgress.total > maxLocalTotal) {
            setMaxLocalTotal(localProgress.total);
        }
        if (errors > maxErrors) {
            setMaxErrors(errors);
        }
        const hasHadUploads = maxRepoTotal > 0 || maxLocalTotal > 0;
        // Repository uploads are finished when repoTotal reaches 0
        const repoUploadsFinished = repoTotal === 0;
        // Local uploads are finished when pending reaches 0
        const localUploadsFinished = localProgress.pending === 0;
        // Upload is finished when both repository and local uploads are complete
        if (hasHadUploads && repoUploadsFinished && localUploadsFinished && !finished) {
            setFinished(true);
            clearLocalFileUploads(fileRepoInfo);
            // Invalidate connector files query to refresh files_in_repo count
            queryClient.invalidateQueries({
                queryKey: getListConnectorFilesQueryKey(),
            });
            queryClient.invalidateQueries({
                queryKey: getListConnectorFilesQueryKey(undefined, true),
            });
        }
        // Reset for next upload cycle when finished and new files are detected
        if (newUploadCycleStarted) {
            setFinished(false);
            setMaxRepoTotal(0);
            setMaxLocalTotal(0);
            setMaxErrors(0);
            setHadProjectImportWorkLimitExceeded(false);
        }
        if (projectImportWorkLimitExceeded) {
            setHadProjectImportWorkLimitExceeded(true);
        }
    }, [
        repoTotal,
        localProgress.total,
        localProgress.pending,
        errors,
        maxRepoTotal,
        maxLocalTotal,
        maxErrors,
        finished,
        queryClient,
        fileRepoInfo,
        clearLocalFileUploads,
        projectImportWorkLimitExceeded,
        newUploadCycleStarted,
    ]);
    return {
        total,
        pending,
        errors: maxErrors,
        uploaded,
        projectOverSizeLimit,
        projectImportWorkLimitExceeded: retainedProjectImportWorkLimitExceeded,
        isFinished: finished,
    };
};
//# sourceMappingURL=useUploadProgress.js.map